import React from 'react';
import { usePermissions } from '../contexts/PermissionsContext';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Lock } from 'lucide-react';

/**
 * Permission-aware Action Button
 * 
 * Renders a button only if user has the required permission.
 * Optionally shows a disabled/locked button when permission is denied.
 * 
 * Usage:
 *   <PermissionButton
 *     module="customers"
 *     action="create"
 *     onClick={handleCreate}
 *     className="..."
 *   >
 *     Add Customer
 *   </PermissionButton>
 */
export const PermissionButton = ({
  module,
  action,
  children,
  showWhenDenied = false,  // Show disabled button when no permission
  tooltipWhenDenied = "You don't have permission for this action",
  ...buttonProps
}) => {
  const { hasPermission, isOwner, loading } = usePermissions();

  // Loading state
  if (loading) {
    return showWhenDenied ? (
      <Button {...buttonProps} disabled>
        {children}
      </Button>
    ) : null;
  }

  // Check permission
  const hasAccess = hasPermission(module, action);

  // Owner always has access
  if (isOwner || hasAccess) {
    return <Button {...buttonProps}>{children}</Button>;
  }

  // No permission
  if (!showWhenDenied) {
    return null; // Hide the button completely
  }

  // Show disabled button with tooltip
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button {...buttonProps} disabled className={`${buttonProps.className || ''} opacity-50 cursor-not-allowed`}>
            <Lock className="w-3.5 h-3.5 mr-1.5" />
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipWhenDenied}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Permission-aware Container
 * 
 * Wraps content that should only be shown if user has permission.
 * 
 * Usage:
 *   <PermissionGate module="customers" action="delete">
 *     <DeleteButton />
 *   </PermissionGate>
 */
export const PermissionGate = ({
  module,
  action,
  children,
  fallback = null  // What to show when permission denied
}) => {
  const { hasPermission, isOwner, loading } = usePermissions();

  if (loading) {
    return fallback;
  }

  const hasAccess = isOwner || hasPermission(module, action);

  if (!hasAccess) {
    return fallback;
  }

  return <>{children}</>;
};

/**
 * Owner-only Container
 * 
 * Wraps content that should only be shown to owners.
 */
export const OwnerOnly = ({ children, fallback = null }) => {
  const { isOwner, loading } = usePermissions();

  if (loading) {
    return fallback;
  }

  if (!isOwner) {
    return fallback;
  }

  return <>{children}</>;
};

/**
 * Staff-only Container
 * 
 * Wraps content that should only be shown to staff.
 */
export const StaffOnly = ({ children, fallback = null }) => {
  const { isStaff, loading } = usePermissions();

  if (loading) {
    return fallback;
  }

  if (!isStaff) {
    return fallback;
  }

  return <>{children}</>;
};

/**
 * Hook for action permissions
 * 
 * Usage:
 *   const { canCreate, canEdit, canDelete, canExport } = useActionPermissions('customers');
 */
export const useActionPermissions = (module) => {
  const { 
    canCreate, 
    canEdit, 
    canDelete, 
    canExport, 
    canApprove, 
    canView,
    isOwner,
    loading 
  } = usePermissions();

  return {
    canView: isOwner || canView(module),
    canCreate: isOwner || canCreate(module),
    canEdit: isOwner || canEdit(module),
    canDelete: isOwner || canDelete(module),
    canExport: isOwner || canExport(module),
    canApprove: isOwner || canApprove(module),
    isOwner,
    loading
  };
};

export default PermissionButton;
